import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, getPromotionMainColor, getPromotionTextColor, getRemotedbUrl, isDesktop } from '../../lib/ep.js';
import { Route, HashRouter as Router, Switch, Link, useParams } from 'react-router-dom';

import ActionButton, { SecondaryButton, SquaredButton } from '../builder/ActionButton.js';
import ActionLink from '../builder/ActionLink.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderMain, { BuilderMainWithoutSidebar } from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import Select from '../builder/Select.js';
import ColorPicker from '../builder/ColorPicker.js';
import ConfirmButton from '../builder/ConfirmButton.js';
import DisabledArea from '../builder/DisabledArea.js';
import DynamicFields from '../builder/DynamicFields.js';
import FieldLabel from '../builder/FieldLabel.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input.js';
import Message, { SuccessMessage } from '../builder/Message.js';
import Model from '../builder/Model.js';
import MultiPicker from '../builder/MultiPicker.js';
import NavigationTabs, { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import Range from '../builder/Range.js';
import RichEditor from '../builder/RichEditor.js';
import SaveButton from '../builder/SaveButton.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import SectionTitle from '../builder/SectionTitle.js';
import TextArea from '../builder/TextArea.js';
import ToggleButton from '../builder/ToggleButton.js';
import Tooltip from '../builder/Tooltip.js';

import Layout from '../front/Layout.js';

import TeamStage from '../../containers/stages/TeamStage.js';

import transferPlaceholder from '../../hoc/transferPlaceholder.js';
import TeamClient, { formatPlayerName, SingleCandidate, SingleCandidateOnly } from '../../containers/TeamClient.js';

import AgentCards from '../ui_items/AgentCards.js';
import CardSelector from '../ui_items/CardSelector.js';
import TeamEquipmentTemplateLayer from '../games/TeamEquipmentTemplateLayer.js';

const RichEditorPlaceHolder = injectIntl(transferPlaceholder(RichEditor));
const InputPlaceHolder = injectIntl(transferPlaceholder(Input));

const candidate_img_w = 60;
const candidate_img_h = 72;
const candidate_img_mult = 4;
const stage_img_w = 400;
const stage_img_h = 600;

export const useTeamStats = (promotion_id,stage_id,fetch_condition=true) => {
	const [stats,setStats] = useState(false);
	useEffect(()=>{
		if (!fetch_condition) return;
		$.get(`/stages/team_stats/${promotion_id}/${stage_id}`,response=>{
			setStats(response);
		},'JSON');
	},[]);

	return stats;
};

const computeScore = (player, result, score_mode = 'exact') => {
	let score = 0;
	for(var player_id in result) {
		if(player[player_id] == undefined) continue;
		if(score_mode == 'exact' && result[player_id].section_id != player[player_id].section_id) continue;
		score++;
	}

	return score;
};

const detectIfTeamHasPlayers = (teams) => {
	if(teams == undefined || teams.length == 0) return false;

	let has_sections = true;
	let has_players = true;
	teams.map((team) => {
		if(has_sections && (team?.sections ?? []).length == 0) has_sections = false;
		if(has_players && (team?.candidates ?? []).length == 0) has_players = false;
	});

	return (has_sections && has_players) ? true : false;
};

const EmptyTeamMessage = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage_id = props?.stage_id ?? false;

	return (
		<BuilderMainWithoutSidebar pm={props.promoModel} not_padded={true}>
			<NavigationTabsBuilder
				relative_selection={true}
				active={`${props.nav_active}`}
				sections={props?.nav_sections ?? []}
			>{props.stage_indicator}</NavigationTabsBuilder>

			<BuilderCanvas
				is_adaptable={false}
				not_awesome={true}
				add_public_css_vars={false}
			>
				<div>
					{i18n("This section will be enabled when you have added players and their positions in the team.")}

					{(stage_id !== false) && (
						<ButtonCollection _class={{other:'gui_not_in_top_small'}}>
							<ActionLink _href={`#stages/${stage_id}/team/settings`}>Add players and positions</ActionLink>
						</ButtonCollection>
					)}
				</div>
			</BuilderCanvas>
		</BuilderMainWithoutSidebar>
	)
};

const TeamCanvasInBuilder = ({ stage, change, ...props }) => {
	const is_test = props?.is_test ?? false;
	const is_editing = (!is_test && props.is_editing != undefined) ? props.is_editing : false;
	const display_empty_message = props?.display_empty_message ?? false;
	const display_ui = props?.display_ui ?? true;

	if(display_empty_message !== false) {
		return (
			<BuilderCanvas
				fit_to_window="content"
				off_content={props.header_message}
			>
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promo_menu}
					layout_extra_classes="is_new_game empty_team_builder"
					only_contents={true}
				>
					<div style={{width:'100%', height:'100%'}}>
						<img src={`${window.cdn_url}img/blank_1x1.png`} width="100%" />

						<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
							<p>{display_empty_message}</p>
						</div>
					</div>
				</Layout>
			</BuilderCanvas>
		)
	}

	const areaChanged = (type, area) => {
		if(is_test) return;

		stage.teams.map((team) => {
			team.sections.map((section) => {
				if(section.id != type) return;
				section.position = area;
				change('teams', stage.teams);
			})
		})
	};

	const stage_props = {
		stage:{
			Stage:{ ...stage },
		},
		promotion:{
			Promotion:props.promotion,
		},
		is_builder:true,
		is_editing:is_editing,
		onTeamChosen:props.onTeamChosen,
		is_playable:props?.force_playable ?? is_test,
		prizes:props.prizes,
		players:props.players,
		given_prize:props.preview,
		areaChanged:areaChanged,
		initial_state:props.initial_state,
		current_team:props?.current_team ?? 0,
	};

	if(!display_ui) {
		return (
			<BuilderCanvas
				fit_to_window="content"
				extra_classes='centered_game'
				off_content={props.header_message}
			>
				<TeamStage
					{...stage_props}
					display_ui={display_ui}
					canvas_ref={props.canvas_ref}
				/>

				{props.children}
			</BuilderCanvas>
		)
	}

	return (
		<BuilderCanvas
			fit_to_window="content"
			off_content={props.header_message}
		>
			<Layout
				promo={props.promotion}
				is_builder={true}
				page="quiz"
				menus={props.promo_menu}
				display_header={props?.display_header ?? null}
				layout_extra_classes="is_new_game"
			>
				<TeamStage
					{...stage_props}
				/>

				{props.children}
			</Layout>
		</BuilderCanvas>
	)
};

const TeamCandidate = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ uploading, setUploading ] = useState(false);

	const autouploader_props = {
		auto_url:`/promotions/auto_img/${props.promotion_id}`,
		auto_type:'stages',
		display_custom_dragging_instructions:"Upload the image",
		_size:{
			width:candidate_img_w * candidate_img_mult,
			height:candidate_img_h * candidate_img_mult,
		},
	};

	return (
		<DisabledArea disabled={uploading}>
			<Model
				model={model}
				change={change}
				errors={props.errors != undefined && props?.errors[model.id]}
			>
				<Input
					name="name"
					_class={{size:60}}
					autotranslate={false}
					value={model?.name ?? i18n("Player name")}
				>{i18n("***Name")}</Input>

				<Input
					name="dorsal"
					max_length={2}
					_class={{size:50}}
				>T-shirt number</Input>

				<AutoPostImageUploader
					image={model.full_image}
					name='image'
					small_input={true}
					_class={{size:50}}
					{...autouploader_props}
				>Image</AutoPostImageUploader>
			</Model>
		</DisabledArea>
	)
}

const SingleTeam = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ uploading, setUploading ] = useState(false);

	const autouploader_props = {
		auto_url:`/promotions/auto_img/${props.promotion_id}`,
		auto_type:'stages',
		display_custom_dragging_instructions:"Upload the image",
		_size:{
			fit:200,
		},
	};

	return (
		<DisabledArea disabled={uploading}>
			<Model
				model={model}
				change={change}
				errors={props.errors != undefined && props?.errors[model.id]}
			>
				<Input
					name="name"
					_class={{first:true, size:50}}
					autotranslate={false}
					placeholder="Your team name"
				>{i18n("***Name")}</Input>

				<Input
					name="short_name"
					_class={{size:50}}
					max_length={5}
				>Short name</Input>

				<AutoPostImageUploader
					image={model.full_team_image}
					name='team_image'
					small_input={true}
					{...autouploader_props}
				>Team image</AutoPostImageUploader>
			</Model>
		</DisabledArea>
	)
}

const TeamSection = ({ stage, model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_template_layer, setDisplayTemplateLayer ] = useState(false);

	const autouploader_props = {
		auto_url:`/promotions/auto_img/${stage.promotion_id}`,
		auto_type:'stages',
		display_custom_dragging_instructions:"Upload the image",
		small_input:true,
		_size:{
			width:candidate_img_w * candidate_img_mult,
			height:candidate_img_h * candidate_img_mult,
		},
	};

	const deletePlayerFromSection = (player_id) => {
		change('candidates', model.candidates.filter((c) => (c != player_id) ? true : false));
	};

	return (
		<div>
			<Model
				model={model}
				change={change}
				errors={props.errors != undefined && props?.errors[model.id]}
			>
				<Input
					name="name"
					_class={{size:60}}
					autotranslate={false}
				>{i18n("***Name")}</Input>

				<Input
					name="min"
					_class={{size:60}}
					placeholder="1"
				>Min. players</Input>

				<Input
					name="max"
					_class={{size:60}}
					placeholder="1"
				>Max. players</Input>

				{(stage.team_scoring == 'exact') && (
					<Input
						name="points"
						_class={{size:60}}
						placeholder="1"
					>Points per hit</Input>
				)}

				<ColorPicker
					name="color"
					color={model?.color ?? getPromotionMainColor(props?.promotion ?? {})}
					_class={{size:60}}
				>Background color</ColorPicker>

				<ColorPicker
					name="text_color"
					color={model?.text_color ?? getPromotionTextColor(props?.promotion ?? {})}
					_class={{size:60}}
				>Text color</ColorPicker>

				<AutoPostImageUploader
					image={model.full_decoration_image}
					name='decoration_image'
					small_input={true}
					{...autouploader_props}
					label_props={{
						_extra_component:(isDesktop()) ? <ActionLink onClick={() => setDisplayTemplateLayer(true)}>Info</ActionLink> : null,
					}}
				>Background image</AutoPostImageUploader>
			</Model>

			<MultiPicker
				items={props.candidates}
				selected_items={props.candidates.filter((c) => ((model?.candidates ?? []).includes(c.id)))}
				title="Players"
				open_button_label="Add players"
				layer_title="Add players to the position"
				layer_subtitle={`${i18n("Section")}: <strong>${model?.name}</strong>`}
				change={props.change_section_players}
				parent_id={model.id}
				intl={props.intl}
				item_icon='knk-football-team-shirt'
				deleteItem={deletePlayerFromSection}
				filterFunc={(item, value) => {
					let regex = new RegExp(value, 'i');
					return regex.test(item.name + item.dorsal);
				}}
				formatItemName={(item) => {
					return formatPlayerName(item, props.intl);
				}}
			/>

			{(display_template_layer) && (
				<TeamEquipmentTemplateLayer
					sport_type={stage?.team_sport_type ?? 'football'}
					close={() => setDisplayTemplateLayer(false)}
					design_url={`#stages/${stage.id}/team/design`}
				/>
			)}
		</div>
	)
}

const consistency_checker = (candidates, sections) => {
	let should_save_sections = false;
	const candidate_ids = candidates.map((c) => (c.id));

	sections.map((section) => {
		if(section.candidates == undefined) return;

		section.candidates = section.candidates.filter((c) => {
			const has_it = candidate_ids.includes(c);
			if(!has_it) should_save_sections = true;
			return has_it;
		});

		return section;
	});

	return (should_save_sections) ? sections : false;
};

const TeamSettings = ({ stage, state, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	
	const [ display_layer_team, setDisplayLayerTeam ] = useState(false);

	const sport_type = stage?.team_sport_type ?? 'football';
	let sport_icon = '';
	switch(sport_type) {
		case 'football':
		case 'basketball':
			sport_icon = `knk-${sport_type}-ball`;
			break;

		default:
			sport_icon = 'knk-entries-sided';
			break;

	}

	const raw_teams = stage?.teams ?? [];
	let teams = [];
	raw_teams.map((team, t) => {
		teams.push({
			title:<>
				{team?.name ?? `${i18n("Team")} #${t + 1}`}

				<span> (<ActionLink
					onClick={() => setDisplayLayerTeam(team.id)}
					autotranslate={false}
				>{i18n("Edit", 'lowercase')}</ActionLink>)</span>
			</>,
			id:team.id,
			icon:sport_icon,
			full_image:team?.full_team_image ?? null,
			ribbons:[[{
				title:props.intl.formatMessage({ id:"{total} players in team" }, { total:(team?.candidates ?? []).length }),
				color:'gray',
			}]],
			description:(team.short_name != undefined && team.short_name != "") ? `(${team.short_name})` : null,
		})
	});

	const addTeam = () => {
		const candidates = candidatesTemplate(stage.team_sport_type);
		change('teams', [
			...raw_teams,
			teamTemplate(stage.team_sport_type, 1, 2, candidates),
		]);
	};

	const deleteTeam = (id) => {
		change('teams',raw_teams.filter((team) => (team.id != id)));
		if (stage.team_result) {
			for (var i in stage.team_result) {
				if (stage.team_result[i].team_id != id)continue;
				delete(stage.team_result[i]);
			}
			change('team_result',{...stage.team_result});
		}
		saveSettings(() => {});
	};

	const saveSettings = (cb) => {
		props.saveStage(cb);
	};

	const changeTeams = (k, v) => {
		change('teams', stage.teams.map((team) => {
			if(team.id != display_layer_team) return team;
			return {...team,[k]:v};
		}));
	};

	let main_card_class = ['team_viewer'];
	if(teams.length == 1) {
		main_card_class.push('is_single_team');
	} else {
		main_card_class.push('is_vs_teams');
	}

	const can_delete = (teams.length == 1) ? false : true;

	return (
		<BuilderMainWithoutSidebar pm={props.promoModel} has_tabs={true} full_main_bg={false}>
			<NavigationTabsBuilder
				relative_selection={true}
				active={`${props.nav_active}`}
				sections={props?.nav_sections ?? []}
			>{props.stage_indicator}</NavigationTabsBuilder>

			<div class="content_builder without_sidebar gui_is_not_padded gui_full_height">
				<section class="not_awesome">
					<AgentCards
						items={teams}
						_class={main_card_class.join(" ")}
						actions={(id) => (<>
							<Link
								to={`/edit/${id}/candidates`}
								className='alone_button gui_flex_submit mainButton gui_pos_rel in_top'
							>{i18n("Edit players")}</Link>
						</>)}
						extra_actions={(can_delete) ? (id) => (
							<ConfirmButton
								color='white'
								icon='knk-delete redIcon'
								onClick={() => deleteTeam(id)}
							>Delete team</ConfirmButton>
						) : null}
					>
						{(teams.length == 1) && (
							<div class="add_team">
								<SectionSubtitle
									onClick={() => addTeam()}
									_class={{other:'knk-add-item is_fullscreen relative_to_container gui_not_bordered'}}
								/>

								<p class="add_label">{i18n("Opponent")}</p>
							</div>
						)}
					</AgentCards>
				</section>
			</div>

			{(display_layer_team !== false) && (
				<FloatingLayer _class={{size:'small'}} backButton={() => setDisplayLayerTeam(false)}>
					<div>
						<SectionTitle>Team settings</SectionTitle>

						<SingleTeam
							model={raw_teams.find((t) => ( t.id == display_layer_team ))}
							promotion_id={stage.promotion_id}
							change={changeTeams}
							stage={stage}
							errors={{}}
							intl={props.intl}
						/>

						<ButtonCollection in_floating={true}>
							<SaveButton
								onClick={(e, cb) => saveSettings(cb)}
							>Save changes</SaveButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</BuilderMainWithoutSidebar>
	)
};

const TeamEdit = ({ stage, state, change, ...props }) => {
	const { id } = useParams();
	const basename = `${props.basename}/edit/${id}`;
	
	const nav_sections = [
		{ label:"Go back", link:`${props.basename}/settings`, icon:'knk-go-prev' },
		{ label:"Players", link:`${basename}/candidates`, icon:'knk-users' },
		{ label:"Positions", link:`${basename}/positions`, icon:'knk-city' },
	];
	
	const team = stage.teams.find((t) => ( t.id == id ));

	const changeTeam = (v) => {
		const new_teams = stage.teams.map((team) => {
			if(team.id != id) return team;
			return v;
		})

		change('teams',new_teams);
		props.saveStage();
	};

	let current_team = 0;

	stage.teams.map((_team, t) => {
		if(_team.id == id) {
			current_team = t;
		}
	});

	return (
		<>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/candidates">
						<TeamCandidates
							stage={stage}
							changeTeam={changeTeam}
							intl={props.intl}
							nav_sections={nav_sections}
							nav_active={`/candidates`}
							candidates={team?.candidates ?? []}
							current_team={current_team}
							team={team}
							promoModel={props.promoModel}
						/>
					</Route>

					<Route exact={true} path="/positions">
						<TeamSections
							stage={stage}
							changeTeam={changeTeam}
							intl={props.intl}
							nav_sections={nav_sections}
							promotion={props.promotion}
							nav_active={`/positions`}
							candidates={team?.candidates ?? []}
							sections={team?.sections ?? []}
							current_team={current_team}
							team={team}
							promoModel={props.promoModel}
						/>
					</Route>
				</Switch>
			</Router>
		</>
	)
};

const TeamCandidates = ({ stage, state, changeTeam, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ candidates, setCandidates ] = useState(props.candidates);
	const team = props?.team ?? false;

	const save_candidates = (cb) => {
		const save_team = {...team,candidates};

		if(team.candidates != undefined && candidates.length != team.candidates.length) {
			const update_sections = consistency_checker(candidates, team?.sections ?? []);
			if(update_sections !== false) {
				save_team.sections = update_sections;
			}
		}

		changeTeam(save_team);
		cb();
	};

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar onSave={save_candidates}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Message
					icon='knk-football-team-shirt'
					_class={{ other:'in_top in_bottom' }}
				>
					<p><FormattedMessage id="You're editing the players for {team}" values={{ team:<strong>{team?.name ?? `${i18n("Team")} #${props.current_team}`}</strong> }} /></p>
				</Message>

				<article class="gui_not_in_top_huge">
					<DynamicFields
						section_title="Players"
						section_icon='knk-users'
						onChange={setCandidates}
						has_translation={true}
						locales={avail_locales}
						add_upload={{
							auto_url:`/promotions/auto_img/${stage.promotion_id}`,
							auto_type:'stages',
							contract:{ name:'name', image:'image' }
						}}
						img_size={{
							width:candidate_img_w * candidate_img_mult,
							height:candidate_img_h * candidate_img_mult,
						}}
						add_options={['bulk','images']}
						field_types={[{
							type:'team_candidate',
							label:"Add player",
							translate:[
								{key:'name',type:'input'}
							],
							title:(f) => { return formatPlayerName(f, props.intl); },
						}]}
						sortable={true}
						fields={candidates}
						ul_styles={{ maxHeight:'calc(100dvh - 300px)' }}
						delete_all={true}
					>
						<TeamCandidate
							promotion_id={stage.promotion_id}
							errors={{}}
							intl={props.intl}
						/>
					</DynamicFields>
				</article>
			</BuilderSidebar>
			
			<BuilderCanvas is_adaptable={false} _bg_styles={{overflowX:'hidden'}}>
				<div class="not_awesome">
					<section class="team_configuration only_list">
						<ul class="candidates_in_section">
							{candidates.map((candidate, can) => {
								return (
									<SingleCandidate
										key={`candidate_${can}`}
										candidate={candidate}
										stage={stage}
										only_list={true}
									>
										<SingleCandidateOnly />
									</SingleCandidate>
								)
							})}
						</ul>
					</section>
				</div>
			</BuilderCanvas>
		</BuilderMain>
	)
};

const filterFunc = (candidate, value) => {
	let regex = new RegExp(value, 'i');
	return regex.test(candidate.name + candidate.dorsal);
};

const TeamSections = ({ stage, changeTeam, dispatch, state, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ adding_candidates, setAddingCandidates ] = useState(false);
	const [ sections, setSections ] = useState(props.sections);

	const team = props?.team ?? {};

	const change_section_players = (players, id) => {
		sections.find((s) => s.id == id).candidates = players;
		change_sections([...sections]);
	};

	const change_sections = (v) => {
		if(v.length != sections.length) {
			v = v.map((section) => {
				delete section.position;
				return section;
			})
		}
		setSections(v);
	};

	const save_sections = (cb) => {
		changeTeam({...team,sections});
		cb();
	};

	const change = (k,v) => {
		changeTeam({...team,[k]:v});
	}

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar onSave={save_sections}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Message
					icon='knk-football-team-shirt'
					_class={{ other:'in_top in_bottom' }}
				>
					<p><FormattedMessage id="You're editing the positions for {team}" values={{ team:<strong>{team?.name ?? `${i18n("Team")} #${props.current_team}`}</strong> }} /></p>
				</Message>

				<article class="gui_not_in_top_huge">
					<DynamicFields
						section_title="Positions"
						section_icon='knk-city'
						onChange={change_sections}
						has_translation={true}
						locales={avail_locales}
						field_types={[{
							type:'team_section',
							label:"Add position",
							translate:[
								{key:'name',type:'input'}
							],
							title:(f) => (f?.name ?? i18n("Position")),
						}]}
						fields={sections}
					>
						<TeamSection
							edit={setAddingCandidates}
							candidates={props.candidates}
							promotion_id={stage.promotion_id}
							promotion={props.promotion}
							stage={stage}
							errors={{}}
							intl={props.intl}
							change_section_players={change_section_players}
						/>
					</DynamicFields>

					<Model model={team} change={change}>
						<Input
							name="num_players"
							placeholder="5"
							_class={{size:50}}
						>Total players</Input>

						{(sections.length > 1) && (
							<Checkbox
								name="autocollapse_positions"
							>Collapse positions by default</Checkbox>
						)}
					</Model>
				</article>
			</BuilderSidebar>

			<TeamCanvasInBuilder
				change={change}
				stage={stage}
				sections={sections}
				candidates={props.candidates}
				promotion={props.promotion}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				is_editing={false}
				view={props.view}
				current_team={props.current_team}
				initial_state='chose_team'
			/>
		</BuilderMain>
	)
};

const TeamResult = ({ stage, change, dispatch, state, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ result, setResult ] = useState(stage.team_result);
	const [ is_recalculating, setIsRecalculating ] = useState(false);
	const [ display_recalculate, setDisplayRecalculate ] = useState(false);
	const [ recalculation_resumee, setRecalculationResumee ] = useState(false);
	const [ display_as_percentage, setDisplayAsPercentage ] = useState(false);
	const [ is_team_defined, setIsTeamDefined ] = useState((stage.team_result != undefined) ? true : false);
	const [ team, setTeam ] = useState(0);
	const canvas_ref = useRef();

	const stats = useTeamStats(stage.promotion_id, stage.id);

	const set_result = (v) => {
		const res = stage.team_result || {};
		const new_res = {...res,...v};
		setResult(new_res);
	};

	const confirmTeam = (v, cb) => {
		if(cb != undefined) cb();

		const res = stage.team_result || {};
		const new_res = {...res,...v};

		if(team < stage.teams.length - 1) {
			change('team_result', new_res);
			setResult(new_res);
			setTeam(team + 1);
			return;
		}

		setTimeout(() => {
			saveImage((image, full_image) => {
				change('team_result', new_res);
				setResult(new_res);

				change('team_result_image', image);
				change('full_team_result_image', full_image);
				props.saveStage();
				setIsTeamDefined(true);
			});
		}, 200);
	};

	const recalculate = (cb) => {
		setIsRecalculating(true);

		$.post(getRemotedbUrl(`/stages/recalculate_team/${stage.promotion_id}/${stage.id}`), (result) => {
			// we delay a bit the process so it displays the "processing" state at least 1 second
			setTimeout(() => {
				setRecalculationResumee(result);
				setIsRecalculating(false);
				if(cb != undefined) cb();
			}, 1000);
		}, 'JSON');
	};

	const resetTeam = () => {
		setResult({});
		change('team_result', '');
		setTeam(0);
		setIsTeamDefined(false);
	};

	const saveImage = (cb) => {
		const image = canvas_ref.current.to_image();

		$.post(getRemotedbUrl(`/stages/team_result_image/${stage.promotion_id}/${stage.id}`), {image}, (response) => {
			cb(response.image, response.full_image);
		}, 'JSON');
	};

	const downloadFinalTeam = () => {
		const image = canvas_ref.current.to_image();
		var a = document.createElement('a');
		a.href = image;
		a.download = "final_team.jpeg";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const scoring_types = [
		{ k:'simple', v:"Per player" },
		{ k:'exact', v:"Per player and position" },
	];

	let sections = [];
	stage.teams.map((_team, t) => {
		let team_name = _team?.short_name ?? (_team?.name ?? "");
		if(team_name == "") team_name = `${i18n("Team")} #${(t + 1)}`;

		sections.push({
			label:team_name,
			icon:'knk-football-team-shirt',
			onClick:() => setTeam(t),
			selected:(team == t) ? true : false,
		});
	});

	if(!detectIfTeamHasPlayers(stage.teams)) {
		return (
			<EmptyTeamMessage
				stage_id={stage.id}
				nav_active={props.nav_active}
				nav_sections={props.nav_sections}
				promoModel={props.promoModel}
				intl={props.intl}
			/>
		)
	}

	if(!is_team_defined) {
		return (
			<BuilderMain pm={props.promoModel}>
				<BuilderSidebar onSave={props.saveStage}>
					<NavigationTabsBuilder
						relative_selection={true}
						active={`${props.nav_active}`}
						sections={props?.nav_sections ?? []}
					>{props.stage_indicator}</NavigationTabsBuilder>

					<NavigationTabs
						active={team}
						sections={sections}
						_class={{other:'not_in_builder team_picker_tabs is_not_bordered'}}
						force_show={true}
						children_class="gui_is_not_padded"
					>
						<ButtonCollection>
							<ToggleButton
								squared={true}
								active={display_as_percentage}
								color="transparent"
								icon='whiteIcon blueBg knk-percent'
								icon_off='blueIcon knk-percent'
								onClick={() => setDisplayAsPercentage(!display_as_percentage)}
								tooltip={<Tooltip position='left' with_icon={false}>{(!display_as_percentage) ? "Click to sort by percentage" : "Click to sort by T-shirt number"}</Tooltip>}
							/>
						</ButtonCollection>
					</NavigationTabs>

					<article class="result_team_picker">
						<TeamClient
							key={`list_${team}`}
							stage={stage}
							is_builder={true}
							team={stage.teams[team]}
							promotion={props.promotion}
							result_pick={true}
							onSelect={set_result}
							next={confirmTeam}
							players={result}
							candidate_order={(display_as_percentage) ? 'percentage' : 'id'}
							stats={stats}
						/>
					</article>
				</BuilderSidebar>

				<TeamCanvasInBuilder
					change={change}
					stage={stage}
					players={result}
					promotion={props.promotion}
					promo_menu={props.promoModel.Custom__Menus}
					intl={props.intl}
					initial_state='position_team'
					display_ui={false}
					force_playable={true}
					canvas_ref={(ref) => canvas_ref.current = ref}
					display_empty_message={(Object.keys((result != undefined) ? result : {}).length == 0) ? i18n("Please, first select the final team among the players list.") : false}
					header_message={<>
						<Message
							title="Select the players who form the winner team"
							_class={{ other:'in_top in_bottom' }}
						>
							<p>{i18n("Participants will score points by matching their team with the winner team.")} {i18n("Once you have confirmed the winner team, you will have the options to manage the scoring mode and publish the winner team.")} <strong>{i18n("You can also reset the winner team anytime.")}</strong></p>
						</Message>
					</>}
				>
					<div class={`owner_download_btn not_awesome${(is_team_defined) ? ' visible' : ''}`}>
						<SquaredButton
							icon="knk-download"
							color={false}
							onClick={() => downloadFinalTeam()}
							tooltip={<Tooltip with_icon={false} position='top'>Download image</Tooltip>}
							aria-label="Download image"
						/>
					</div>
				</TeamCanvasInBuilder>
			</BuilderMain>
		)
	}

	const publish_team_results = [
		{ k:'0', v:i18n("No") },
		{ k:'only_logged', v:i18n("Yes, only visible to participants") },
		{ k:'all', v:i18n("Yes, visible to everyone") },
	];

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Select
							_class={{ first:true, size:50 }}
							name="team_scoring"
							values={scoring_types}
							label_props={{
								tooltip:<Tooltip position='bottom' autotranslate={false}>{`<strong>${i18n("Per player")}:</strong> ${i18n("1 point will be given for each player who appears in the final team, even if the position does not match.")}<br /><br /><strong>${i18n("Per player and position")}:</strong> ${i18n("1 point will be given for each player who appears in the final team and also matches the position.")}`}</Tooltip>,
							}}
						>Scoring mode</Select>

						<Input name="team_points_per_hit" placeholder="1" _class={{ size:50 }}>Awarded points per hit</Input>
					</Model>

					<Message>
						<p><FormattedMessage id="Click on the {button} button to update the points of each participant based on the winner team and the scoring mode." values={{ button:<strong>{i18n("Recalculate scores")}</strong>}} />{i18n("")}</p>

						<p><FormattedMessage id="To change players of the winner team, click on {link}." values={{ link:<ActionLink onClick={() => resetTeam()}>reset winner team</ActionLink>}} /></p>
					</Message>

					<div class="not_in_top">
						<ButtonCollection _class={{other:'gui_flex_end gui_not_in_bottom_small'}}>
							<ActionButton
								disabled={!is_team_defined}
								onClick={() => setDisplayRecalculate(true)}
							>Recalculate scores</ActionButton>
						</ButtonCollection>
					</div>
				</article>

				<article class="gui_not_in_top_huge">
					<Model model={stage} change={change}>
						<Select
							name='team_results_publish'
							values={publish_team_results}
						>Do you want to publish a page with the winner team?</Select>
					</Model>

					{(stage.team_results_publish != undefined && stage.team_results_publish != '0') && (
						<Model model={stage} change={change}>
							<InputPlaceHolder
								name='team_results_button'
								placeholder={i18n("See winner team")}
							>Link title to the page</InputPlaceHolder>

							<RichEditorPlaceHolder
								name='team_results_description'
								placeholder={`<h1>${i18n("Winner team")}</h1>`}
							>Introduction message</RichEditorPlaceHolder>
						</Model>
					)}
				</article>
			</BuilderSidebar>

			<TeamCanvasInBuilder
				change={change}
				stage={stage}
				//players={result}
				promotion={props.promotion}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				//preview={preview}
				initial_state='winner_team'
				//display_header={filters.view_page == 'success'}
			/>

			{(display_recalculate) && (
				<FloatingLayer
					_class={{size:'small'}}
					enable_close={(is_recalculating) ? false : true}
					backButton={(!is_recalculating !== false) ? () => setDisplayRecalculate(false) : null}
				>
					<div>
						<SectionTitle _class={{icon:'knk-reload'}}>Recalculate scores for participants</SectionTitle>
						<SectionSubtitle>This process might take a while.</SectionSubtitle>
						<SectionSubtitle autotranslate={false}><strong>{i18n("Please don't close this layer or refresh the page until the end confimation appears.")}</strong></SectionSubtitle>

						{(recalculation_resumee !== false) && (
							<SuccessMessage>
								<p><FormattedMessage id="{total} users updated" values={{ total:recalculation_resumee.updated }} /></p>
							</SuccessMessage>
						)}

						<ButtonCollection in_floating={true}>
							{(recalculation_resumee !== false) ? (
								<SecondaryButton
									onClick={() => {setDisplayRecalculate(false); setRecalculationResumee(false);}}
								>Done!</SecondaryButton>
							) : (
								<SaveButton
									onClick={(e, cb) => recalculate(cb)}
								>Recalculate scores</SaveButton>
							)}
						
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</BuilderMain>
	)
};

const TeamStats = ({ stage, change, dispatch, state, ...props }) => {
	if(!detectIfTeamHasPlayers(stage.teams)) {
		return (
			<EmptyTeamMessage
				stage_id={stage.id}
				nav_active={props.nav_active}
				nav_sections={props.nav_sections}
				promoModel={props.promoModel}
				intl={props.intl}
			/>
		)
	}

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stats = useTeamStats(stage.promotion_id, stage.id);

	const publish_team_stats = [
		{ k:'0', v:i18n("No") },
		{ k:'only_logged', v:i18n("Yes, only visible to participants") },
		{ k:'all', v:i18n("Yes, visible to everyone") },
	];

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Select
							name='team_stats_publish'
							values={publish_team_stats}
							_class={{first:true}}
						>Do you want to publish a page with the statistics?</Select>
					</Model>

					{(stage.team_stats_publish != undefined && stage.team_stats_publish != '0') && (
						<Model model={stage} change={change}>
							<InputPlaceHolder
								name='team_stats_button'
								placeholder={i18n("See statistics")}
							>Link title to the statistics page</InputPlaceHolder>

							<RichEditorPlaceHolder
								name='team_stats_description'
								placeholder={`<h1>${i18n("Statistics")}</h1>`}
							>Introduction message</RichEditorPlaceHolder>
						</Model>
					)}
				</article>
			</BuilderSidebar>

			<TeamCanvasInBuilder
				change={change}
				stage={stage}
				promotion={props.promotion}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				stats={stats}
				initial_state='public_stats'
			/>
		</BuilderMain>
	)
};

const TeamShare = ({ stage, change, state, preview, ...props }) => {
	if(!detectIfTeamHasPlayers(stage.teams)) {
		return (
			<EmptyTeamMessage
				stage_id={stage.id}
				nav_active={props.nav_active}
				nav_sections={props.nav_sections}
				promoModel={props.promoModel}
				intl={props.intl}
			/>
		)
	}

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ filters, setFilters ] = useState({ view_page:'success' });

	const main_description_placeholder = `<h1>${i18n("This is your winning team!")}</h1><p>${i18n("Share it now with your friends so they can also create their own and take part in the competition.")}</p>`;
	const main_shared_placeholder = `<h1>${i18n("The team of {name}")}</h1><p>${i18n("Want to take part in the competition? Build your own team now in less than 2 minutes.")}</p>`;

	// by default, all networks are enabled, so if promotion.config.share is not defined, we set up the networks defaults to '1' (i.e. true):
	const share_options = props?.promotion?.config?.share?.networks ?? { twitter:'1', whatsapp:'1', line:'1', telegram:'1' };

	const has_twitter_enabled = (share_options.twitter == '1') ? true : false;
	const has_mobile_enabled = (share_options.whatsapp == '1' || share_options.line == '1' || share_options.telegram == '1') ? true : false;

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Model model={filters} change={(k, v) => setFilters({ ...filters, [k]:v })}>
					<Select
						name='view_page'
						values={[
							{ k:'success', v:i18n("Page after creating the team") },
							{ k:'public_share', v:i18n("Destination page of the shared link") },
						]}
						_class={{first:true}}
					>Select the page to edit</Select>
				</Model>

				{(filters.view_page == 'success') && (
					<>
						<article class="gui_not_in_top_huge">
							<Model model={stage} change={change}>
								<RichEditorPlaceHolder
									name='main_description'
									placeholder={main_description_placeholder}
								>Introduction message</RichEditorPlaceHolder>

								<InputPlaceHolder
									name='button_label'
									placeholder={i18n("Share")}
								>{i18n("Label for '%s' button").replace('%s', i18n("Share"))}</InputPlaceHolder>

								{/*<InputPlaceHolder
									name='secondary_button_label'
									placeholder={i18n("Finalize")}
								>{i18n("Label for '%s' button").replace('%s', i18n("Finalize"))}</InputPlaceHolder>*/}
							</Model>
						</article>

						<article class="gui_not_in_top_huge">
							<header class="gui_section_header_builder">
								<h3 class="section_title knk-share">{i18n("Content when sharing")}</h3>
							</header>

							<Model model={stage} change={change}>
								<Input
									name="share_title"
									max_length={90}
									label_props={{
										tooltip:<Tooltip>This is the title of the publication of the viral message on Facebook. Maximum 90 characters.</Tooltip>,
									}}
								>Title</Input>

								<TextArea
									name="share_description"
									max_length={255}
									label_props={{
										tooltip:<Tooltip>This is the description of the publication of the viral message on Facebook. Maximum 255 characters.</Tooltip>,
									}}
								>Description</TextArea>
							</Model>

							<DisabledArea
								disabled={(!has_twitter_enabled) ? true : false}
								message="Twitter network must be enabled"
								link='#share'
							>
								<Model model={stage} change={change}>
									<TextArea
										name="share_tweet"
										max_length={280}
										label_props={{
											tooltip:<Tooltip>This is the text of the tweet of the viral message on Twitter. The shared link will be included automatically at the end of this text.</Tooltip>,
										}}
									>Tweet</TextArea>
								</Model>
							</DisabledArea>

							<DisabledArea
								disabled={(!has_mobile_enabled) ? true : false}
								message="Whatsapp, Telegram or Line networks must be enabled"
								link='#share'
							>
								<Model model={stage} change={change}>
									<TextArea
										name="share_instant"
										max_length={200}
										label_props={{
											tooltip:<Tooltip>This is the message that users will send through Instant Messaging Apps. The shared link will be automatically included at the end of the mensaje. This option is only available for users connnected via smartphone. Maximum length: 200 characters</Tooltip>,
										}}
									>Instant Messaging Apps (Whatsapp, Telegram, Line)</TextArea>
								</Model>
							</DisabledArea>
						</article>
					</>
				)}

				{(filters.view_page == 'public_share') && (
					<article class="gui_not_in_top_huge">
						<Model model={stage} change={change}>
							<RichEditorPlaceHolder
								name='team_shared_description'
								placeholder={main_shared_placeholder}
							>Introduction message</RichEditorPlaceHolder>

							<InputPlaceHolder
								name='team_shared_button_label'
								placeholder={i18n("Build my team")}
							>{i18n("Label for '%s' button").replace('%s', i18n("Build my team"))}</InputPlaceHolder>
						</Model>
					</article>
				)}
			</BuilderSidebar>

			<TeamCanvasInBuilder
				change={change}
				stage={stage}
				promotion={props.promotion}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				preview={preview}
				initial_state={filters.view_page}
				display_header={filters.view_page == 'success'}
			/>
		</BuilderMain>
	)
}

const TeamDesign = ({ stage, change, state, ...props }) => {
	if(!detectIfTeamHasPlayers(stage.teams)) {
		return (
			<EmptyTeamMessage
				stage_id={stage.id}
				nav_active={props.nav_active}
				nav_sections={props.nav_sections}
				promoModel={props.promoModel}
				intl={props.intl}
			/>
		)
	}

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const saveStage = (cb) => {
		props.saveStage(cb);
	};

	const autouploader_props = {
		auto_url:`/promotions/auto_img/${stage.promotion_id}`,
		auto_type:'stages',
		display_custom_dragging_instructions:"Upload the image",
		small_input:true,
		_size:{
			width:stage_img_w * 2,
			height:stage_img_h * 2,
		},
	};

	const default_margin = 10;
	const margins = ['top', 'right', 'bottom', 'left'];

	const change_margin = (k, v) => {
		if(stage.team_margin == undefined) {
			stage.team_margin = {
				top:default_margin,
				right:default_margin,
				bottom:default_margin,
				left:default_margin,
			};
		}

		change('team_margin', { ...stage.team_margin, [k]:v });
	};

	const change_scale = (k, v) => {
		change('team_player_scale', (Math.round(v) / 100));
	};

	const player_styles = [
		{ k:'none', v:`${i18n("No mask")} (${i18n("default", 'lowercase')})` },
		{ k:'circle', v:i18n("Circle mask") },
		{ k:'rect', v:i18n("Square mask") },
		{ k:'shirt', v:i18n("T-shirt mask") },
	];

	const promo_type = props.promotion?.promo_type ?? '1';
	const display_watermark = (parseInt(promo_type) < 2) ? true : false;

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar onSave={saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<AutoPostImageUploader
							image={(stage.full_bg_image != "") ? stage.full_bg_image : undefined}
							name='bg_image'
							_class={{first:true}}
							{...autouploader_props}
						>Background image</AutoPostImageUploader>
					</Model>

					<Model model={stage} change={change_scale}>
						<Range
							min={120}
							max={350}
							type='zoom'
							name='team_player_scale'
							value={100 * (stage?.team_player_scale ?? 1)}
							default={140}
							step={10}
							_class={{size:60}}
						>Player scale</Range>
					</Model>

					<Model model={stage} change={change}>
						<Select
							name='team_player_style'
							values={player_styles}
							_class={{size:50}}
							label_props={{
								tooltip:<Tooltip>{`${i18n("If the player has an image, the mask will be applied to the player's image. If the player has no image, the mask will apply to the background image of the player's position.")}<br /><br />${i18n("You can modify this background image from the position settings.")}`}</Tooltip>,
							}}
						>Player style</Select>

						<Checkbox
							name="team_hide_member_names"
							value={stage?.team_hide_member_names ?? false}
						>Hide player names in the field</Checkbox>

						<Checkbox
							name="team_enable_drag_members"
							value={stage?.team_enable_drag_members ?? true}
							label_props={{
								tooltip:<Tooltip autotranslate={false}>{`${i18n("By selecting this option, participants will be able to adjust the position of the players on the pitch manually by drag'n'drop. This option is ideal for participants to customise the field formation of their ideal team.")}<br /><br />${i18n("If you do not select this option, the players will automatically be displayed in the areas you have marked for each position. Participants will not be able to adjust the position of the players.")}`}</Tooltip>,
							}}
						>Allow manual positioning of players</Checkbox>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<header class="gui_section_header_builder">
						<h3 class="section_title knk-obj-rect">{i18n("Margins")}</h3>
					</header>

					<Message>
						<p>{i18n("Use the margins to block some areas so the players can't overlap there.")}</p>
						<p>{i18n("These blocked areas can be used to position some logos or important imformation within the background image.")}</p>
						<p>{i18n("You'll see the blocked area in red in the side preview.")}</p>
					</Message>

					<Model model={stage.team_margin} change={change_margin}>
						{margins.map((margin) => {
							return (
								<Range
									key={`margin_${margin}`}
									min={(display_watermark && margin == 'bottom') ? 30 : 0}
									max={100}
									name={margin}
									value={(stage.team_margin != undefined) ? stage.team_margin[margin] : default_margin}
									default={default_margin}
									step={1}
									_class={{size:60}}
									autotranslate={false}
								>{i18n(`${margin} margin`, 'ucfirst')}</Range>
							)
						})}
					</Model>
				</article>
			</BuilderSidebar>

			<TeamCanvasInBuilder
				change={change}
				stage={stage}
				promotion={props.promotion}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				initial_state='position_team'
				is_editing={true}
			/>
		</BuilderMain>
	)
};


const TeamTest = ({ stage, state, dispatch, change, ...props }) => {
	const [ preview, setPreview ] = useState(false);
	const [ score, setScore ] = useState(0);

	if(!detectIfTeamHasPlayers(stage.teams)) {
		return (
			<EmptyTeamMessage
				stage_id={stage.id}
				nav_active={props.nav_active}
				nav_sections={props.nav_sections}
				promoModel={props.promoModel}
				intl={props.intl}
			/>
		)
	}

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const resetGame = (id) => {
		setPreview(-1);
		setScore(0);
		setTimeout(() => {
			setPreview((id != undefined) ? id : preview);
		}, 0);
	};

	const onTeamChosen = (score)=>{
		setScore(computeScore(score,stage.team_result,stage.team_scoring));
	}

	return (
		<BuilderMain pm={props.promoModel}>
			<BuilderSidebar>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Message
						_class={{ other:'in_top' }}
					>
						<p>{i18n("Score")}: <strong>{score}</strong></p>
					</Message>

					<ButtonCollection>
						<ActionButton
							icon='knk-reload'
							onClick={() => resetGame()}
						>Reset</ActionButton>
					</ButtonCollection>
				</article>
			</BuilderSidebar>

			{(preview == -1) ? (
				null
			) : (
				<TeamCanvasInBuilder
					candidates={props.candidates}
					change={change}
					stage={stage}
					promotion={props.promotion}
					promo_menu={props.promoModel.Custom__Menus}
					onTeamChosen={onTeamChosen}
					is_test={true}
					intl={props.intl}
					preview={preview}
				/>
			)}
		</BuilderMain>
	)
};

const getRandomCandidate = (index) => {
	return {
		id:uuidv4(),
		type:"team_candidate",
		name:`Player ${index}`,
		dorsal:index,
	};
}

const teamTemplate = ( sport, index = 0, num_teams = 1, candidates ) => {
	let sections;

	switch(sport) {
		case 'football':
			sections = [{
				id:uuidv4(),
				name:"Goal keeper",
				type:'team_section',
				min:'1',
				max:'1',
				candidates:[],
			}, {
				id:uuidv4(),
				name:"Defenders",
				type:'team_section',
				min:'2',
				max:'4',
				candidates:[],
			}, {
				id:uuidv4(),
				name:"MidFielders",
				type:'team_section',
				min:'2',
				max:'4',
				candidates:[],
			}, {
				id:uuidv4(),
				name:"Strikers",
				type:'team_section',
				min:'2',
				max:'4',
				candidates:[],
			}];

			var team = {
				id:uuidv4(),
				name:`Team ${index+1}`,
				type:'team',
				candidates:[],
				sections:sections,
				num_players:11
			};

			if(candidates) {
				team.sections[0].candidates = candidates.slice(0, 1).map((c) => ( c.id ));
				team.sections[1].candidates = candidates.slice(1, 5).map((c) => ( c.id ));
				team.sections[2].candidates = candidates.slice(5, 8).map((c) => ( c.id ));
				team.sections[3].candidates = candidates.slice(8, 11).map((c) => ( c.id ));
				team.candidates = candidates;
			}
			return team;

		case 'basketball':
			sections = [{
				id:uuidv4(),
				name:"Guards",
				type:'team_section',
				position:{ w:'0.6',h:1/3/num_teams,x:'0.2',y:0+(index*0.5) },
				min:'1',
				max:'2',
			}, {
				id:uuidv4(),
				name:"Forwards",
				type:'team_section',
				position:{ w:'1',h:1/3/num_teams,x:'0',y:1/3/num_teams+ (index*0.5)},
				min:'1',
				max:'2',
			}, {
				id:uuidv4(),
				name:"Centers",
				type:'team_section',
				position:{ w:'0.6',h:1/3/num_teams,x:'0.2',y:2/3/num_teams+(index*0.5) },
				min:'1',
				max:'2',
			}];

			var team = {
				id:uuidv4(),
				name:`Team ${index+1}`,
				type:'team',
				candidates:[],
				sections:sections,
				num_players:5
			};

			if(candidates) {
				team.sections[0].candidates = candidates.slice(0, 1).map((c) => ( c.id ));
				team.sections[1].candidates = candidates.slice(1, 3).map((c) => ( c.id ));
				team.sections[2].candidates = candidates.slice(3, 6).map((c) => ( c.id ));
				team.candidates = candidates;
			}
			return team;

		default:
			var team = {
				id:uuidv4(),
				name:`Team ${index+1}`,
				type:'team',
				candidates:[],
				sections:[],
				num_players:10
			};
			return team;

	}
}


const candidatesTemplate = (sport) => {
	let num_players = 11;
	switch (sport) {
		case 'football':
			num_players = 11;
			break;
		case 'basketball':
			num_players = 5;
			break;
		default:
			break;
	}
	const candidates = [];
	for(let i = 1; i < num_players + 1; i++) {
		candidates.push(getRandomCandidate(i));
	}
	return candidates;
}

const sportTemplate = (sport,num_teams,change) => {
	let teams =[];
	let player_scale = 1;
	let team_margin = { top:'30', right:'30', bottom:'30', left:'30' };
	for(var i = 0; i < num_teams; i++) {
		teams.push(teamTemplate(sport, i, num_teams, candidatesTemplate(sport)));
	}
	switch (sport) {
		case 'football':
			break;

		case 'basketball':
			player_scale = 1.4;
			team_margin = { top:'30', right:'0', bottom:'30', left:'0' };

		default:
			break;
	}

	change('team_player_scale', player_scale);
	change('team_margin', team_margin);
	change('team_sport_type', sport);
	change('teams', teams);
};

const TeamBuilder = ({ base_route, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const basename = `/stages/${props.stage.id}/team`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Teams", link:`${basename}/settings`, icon:'knk-users' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Share", link:`${basename}/share`, icon:'knk-share' },
		{ label:"Winner team", link:`${basename}/result`, icon:'knk-on' },
		{ label:"Statistics", link:`${basename}/stats`, icon:'knk-stats' },
		{ label:"Test game", link:`${basename}/test`, icon:'knk-developers' },
	];

	const stage = props.stage;

	if(stage.teams == undefined) {
		const [ sport_type, setSportType ] = useState(false);
		const [ play_type, setPlayType ] = useState(false);

		const default_fake_cta = <ButtonCollection _class={{other:'gui_flex_center'}}><ActionButton>Select</ActionButton></ButtonCollection>;

		let sports = [{
			title:i18n("Football"),
			icon:'knk-football-ball',
			description:props.intl.formatMessage({ id:"Quick preconfiguration for the {sport} team editor." }, { sport:i18n("Football", 'lowercase') }),
			enabled:(sport_type == 'football') ? true : false,
			onClick:() => setSportType('football'),
			extra_content:default_fake_cta,
		}, {
			title:i18n("Basketball"),
			icon:'knk-basketball-ball',
			description:props.intl.formatMessage({ id:"Quick preconfiguration for the {sport} team editor." }, { sport:i18n("Basketball", 'lowercase') }),
			enabled:(sport_type == 'basketball') ? true : false,
			onClick:() => setSportType('basketball'),
			extra_content:default_fake_cta,
		}, {
			title:i18n("***Other"),
			icon:'knk-entries-sided',
			description:i18n("The editor includes many options to support any sport."),
			enabled:(sport_type == 'other') ? true : false,
			onClick:() => setSportType('other'),
			extra_content:default_fake_cta,
		}];

		const play_modes = [{
			title:i18n("Single team"),
			icon:'knk-shield',
			description:i18n("The user will build only 1 team of a match."),
			enabled:(play_type == 'single') ? true : false,
			onClick:() => setPlayType('single'),
			extra_content:default_fake_cta,
		}, {
			title:i18n("Two teams"),
			custom_icon:<span><span class="is_icon knk-shield" /><span style={{position:'absolute', fontWeight:'bold', paddingBlockStart:'1.3em', paddingInlineStart:'2px'}}>vs</span><span class="is_icon knk-shield" /></span>,
			description:i18n("The user will build the 2 teams of a match."),
			enabled:(play_type == 'versus') ? true : false,
			onClick:() => setPlayType('versus'),
			extra_content:default_fake_cta,
		}];

		const createGame = (cb) => {
			sportTemplate(sport_type, (play_type == 'versus') ? 2 : 1, props.change);
			if(cb != undefined) cb();
		}

		return (
			<BuilderMainWithoutSidebar pm={props.promoModel} has_tabs={true}>
				<NavigationTabsBuilder
					//relative_selection={true}
					sections={[{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' }]}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<div class="content_builder without_sidebar">
					<section class="not_awesome">
						<div>
							<div class="gui_section_header in_top alone_section_header cleared_header only_title">
								<SectionTitle _class={{other:'in_top'}}>Build your team</SectionTitle>
							</div>

							<div>
								<FieldLabel _class={{other:'in_top'}} autotranslate={false}>1. {i18n("Select a sport")}</FieldLabel>

								<CardSelector
									items={sports}
									promotion={{}}
								/>
							</div>

							<div class="gui_not_in_top_huge">
								<FieldLabel _class={{other:'in_top'}} autotranslate={false}>2. {i18n("Number of teams to build")}</FieldLabel>

								<CardSelector
									items={play_modes}
									promotion={{}}
								/>
							</div>

							<div class="gui_not_in_top_big">
								<ButtonCollection>
									<SaveButton
										disabled={(sport_type === false || play_type === false) ? true : false}
										onClick={(e, cb) => createGame(cb)}
										tooltip={(sport_type === false || play_type === false) ? <Tooltip position='right' with_icon={false}>First, select above the sport and the number of teams</Tooltip> : null}
									>Continue</SaveButton>
								</ButtonCollection>
							</div>
						</div>
					</section>
				</div>
			</BuilderMainWithoutSidebar>
		)
	}

	const baseProps = {
		change:props.change,
		intl:props.intl,
		nav_sections:nav_sections,
		stage:stage,
		saveStage:props.saveStage,
		promotion:props.promotion,
		promoModel:props.promoModel,
		stage_indicator:props.stage_indicator,
	}
	
	return (
		<Router basename={basename} hashType="noslash">
			<Switch>
				<Route exact={true} path="/settings">
					<TeamSettings
						{...baseProps}
						nav_active={`/settings`}
					/>
				</Route>

				<Route path="/edit/:id">
					<TeamEdit
						{...baseProps}
						basename={basename}
						nav_active={`/edit`}
					/>
				</Route>

				<Route exact={true} path="/design">
					<TeamDesign
						{...baseProps}
						nav_active={`/design`}
					/>
				</Route>

				<Route exact={true} path="/share">
					<TeamShare
						{...baseProps}
						nav_active={`/share`}
					/>
				</Route>

				<Route exact={true} path="/result">
					<TeamResult
						{...baseProps}
						nav_active={`/result`}
					/>
				</Route>

				<Route exact={true} path="/stats">
					<TeamStats
						{...baseProps}
						nav_active={`/stats`}
					/>
				</Route>

				<Route exact={true} path="/test">
					<TeamTest
						{...baseProps}
						nav_active={`/test`}
					/>
				</Route>
			</Switch>
		</Router>
	)
};

export default injectIntl(TeamBuilder);